import React from 'react';
import $ from 'jquery';
import { Grid, IconButton, Paper, Stack, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import { selectedSidepanelName } from '../../Redux/SelectedSidepanelSlice';
import { ReactComponent as GoBackOutlined } from '../../assets/sidepanel/back.svg';
import useTranslation from '../../hooks/useTranslation';

import PdfViewer from '../PdfViewer';
import SidebarMenu from './Sidebar/Menu';

import SidepanelAnnotations from './Sidebar/Elements/Annotations';
import SidepanelAnnotationProperties from './Sidebar/Elements/AnnotationProperties';
import SidepanelAttachments from './Sidebar/Elements/Attachments';
import SidepanelBookmarks from './Sidebar/Elements/Bookmarks';
import SidepanelCompare from './Sidebar/Elements/Compare';
import SidepanelLayers from './Sidebar/Elements/Layers';
import SidepanelSearch from './Sidebar/Elements/Search';
import SidepanelSignatures from './Sidebar/Elements/Signatures';
import SidepanelThumbnails from './Sidebar/Elements/Thumbnails';

import styles from './MainContent.module.scss';
import { useDarkMode } from '../../DarkModeContext';
import { ReduxUpdater } from '../../pdf/ReduxUpdater';
import { GlobalDialogs } from '../../pdf/GlobalDialogs';
import type { DocumentApiHistory, UserApiConfiguration } from '../../types';
import { devError, devLog, devWarning } from '../../utils';
import { PdfViewerViewModeFlag, isValidViewMode } from '../../pdf/Enums';
import { backendApiUrl } from '../../App';
import { GlobalConstants } from '../../pdf/GlobalConstants';
import { eplassSliceSelections } from '../../Redux/EplassSlice';
import { ApryseSDK } from '../../pdf/ApryseSDK';
import { selectedStateName } from '../../Redux/SelectedStateSlice';
import { initSettingsError } from '../../Redux/InitSettingsSlice';
interface IMainContentProps {
    customFontURL?: string;
    language: string;
    license: string;
}

export default (props: IMainContentProps) => {
    const i18next_t = useTranslation();
    const { isDark } = useDarkMode();

    const selectedSidepanel = useSelector(selectedSidepanelName);
    const selections = useSelector(eplassSliceSelections);
    const stateName = useSelector(selectedStateName);
    const initError = useSelector(initSettingsError);

    // const refreshFitZoom = React.useCallback(() => {
    //     // ToDO: // If not an overlay and zoom mode is one of the string values, adjust zoom
    //     // if (context && GlobalVariables.docRender) {
    //     //     const zoom = context.docRender.getScale();

    //     //     if (typeof zoom === 'string') {
    //     //         GlobalVariables.docRender.pdfViewer.zoomTo(zoom).catch(GlobalDialogs.showError);
    //     //     }
    //     // }
    // }, [context]);

    const onResize = (): void => {
        // ToDO: debounce(refreshFitZoom, 250);
    };
    
    // Fit zoom when resizing the window.
    React.useEffect(() => {
        if (!ReduxUpdater.lists?.userStamps?.length) {
            ApryseSDK.loadUserStamps();
        }
        ApryseSDK.loadUserCategories();

        $(window).on('resize', onResize);

        return () => {
            $(window).off('resize', onResize);
        };
    });

    const onPdfOpened = async () => {
        devLog('onPdfOpened...');
        /**
         * Since appending an element later on causes the element
         * to be at the end of the document... a workaround for this
         * is adding it right at the beginning (as a hidden placeholder)...
         */
        $('.viewer-container').append('<div id="measurement-info" style="display: none;"></div>');
        ApryseSDK.setLanguage(props.language);

        if (process.env.REACT_APP_IGNORE_USER_CONFIG?.toLocaleLowerCase() === 'true') {
            ReduxUpdater.viewMode =
                ReduxUpdater.documentURL2 !== ''
                    ? PdfViewerViewModeFlag.Compare
                    : PdfViewerViewModeFlag.View;
            devWarning(
                'Fetched user config will not be applied: ignore-flag is set in dev-env variables',
            );
            return;
        }

        const viewerKey = [
            ...GlobalConstants.supported.documents,
            ...GlobalConstants.supported.images,
        ]
            .map(extension => ReduxUpdater.documentURL?.endsWith(extension))
            .some(v => v === true)
            ? ''
            : ReduxUpdater.documentURL;

        try {
            let request: RequestInit | undefined = undefined;

            if (viewerKey) {
                request = {
                    headers: {
                        viewerKey
                    }
                };
            }

            const response = await fetch(backendApiUrl.user.settings, request);

            if (!response.ok) {
                throw new Error('Failed to fetch user settings.');
            }

            const settingsData = (await response.json()) as UserApiConfiguration;

            if (ReduxUpdater.documentURL2 !== '') {
                ReduxUpdater.viewMode = PdfViewerViewModeFlag.Compare;
            } else if (isValidViewMode(settingsData.viewMode)) {
                ReduxUpdater.viewMode = settingsData.viewMode;
            } else {
                ReduxUpdater.viewMode = PdfViewerViewModeFlag.View;
            }

            if (settingsData?.presets) {
                if (settingsData.presets.lists) {
                    ReduxUpdater.lists = settingsData.presets.lists;
                }

                if (settingsData.presets.selected?.color) {
                    ReduxUpdater.selectedColor = settingsData.presets.selected.color;
                    ReduxUpdater.useWorkflowColor =
                        settingsData.presets.lists?.colors?.length === 1;
                }
            }
        } catch (error) {
            onUserConfigError(error as Error);
        }

        if (ReduxUpdater.documentURL) {
            try {
                const request: RequestInit = {
                    headers: {
                        viewerKey: ReduxUpdater.documentURL
                    }
                };
                const response1 = await fetch(backendApiUrl.document.info, request);

                if (!response1.ok) {
                    throw new Error('Failed to fetch document info.');
                }

                const documentData = (await response1.json()) as DocumentApiHistory;

                devLog(documentData);
                ReduxUpdater.documentName = documentData.docNumber;
                ReduxUpdater.documentNoteID = documentData.NoteId;
                ReduxUpdater.documentVersion = documentData.docIndex;
                ReduxUpdater.documentSubject = documentData.subject;
                ReduxUpdater.annotationSidepanelRefresh();

                const response2 = await fetch(backendApiUrl.document.history, request);

                if (!response2.ok) {
                    throw new Error('Failed to fetch document history');
                }

                ReduxUpdater.documentHistory = (await response2.json()) as DocumentApiHistory[];
                ReduxUpdater.annotationSidepanelRefresh();
            } catch (error) {
                onUserConfigError(error as Error);
            }
        }

        if (ReduxUpdater.documentURL2) {
            try {
                const request: RequestInit = {
                    headers: {
                        viewerKey: ReduxUpdater.documentURL2
                    }
                };
                const response1 = await fetch(backendApiUrl.document.info, request);

                if (!response1.ok) {
                    throw new Error('Failed to fetch document info.');
                }

                const documentData = (await response1.json()) as DocumentApiHistory;

                devLog(documentData);
                ReduxUpdater.documentName2 = documentData.docNumber;
                ReduxUpdater.documentNoteID2 = documentData.NoteId;
                ReduxUpdater.documentVersion2 = documentData.docIndex;
                ReduxUpdater.documentSubject2 = documentData.subject;
                ReduxUpdater.annotationSidepanelRefresh();

                const response2 = await fetch(backendApiUrl.document.history, request);

                if (!response2.ok) {
                    throw new Error('Failed to fetch document history.');
                }

                ReduxUpdater.documentHistory2 = (await response2.json()) as DocumentApiHistory[];
                ReduxUpdater.annotationSidepanelRefresh();
            } catch (error) {
                onUserConfigError(error as Error);
            }
        }

        // Show any stored messages and empty queue
        GlobalDialogs.showNotifications();
    };

    const onUserConfigError = (error: Error) => {
        devLog('Error fetching user config or ratio data.');
        devError(error);
        ReduxUpdater.viewMode = PdfViewerViewModeFlag.View;
    };

    const onPdfError = (error: Error) => {
        devLog('Error opening pdf file');
        devError(error);
        ReduxUpdater.viewMode = PdfViewerViewModeFlag.Error;
    };

    const sidepanelContent = React.useMemo(() => {
        switch (selectedSidepanel) {
            case 'annotations':
                return <SidepanelAnnotations />;
            case 'attachments':
                return <SidepanelAttachments />;
            case 'bookmarks':
                return <SidepanelBookmarks />;
            case 'compare':
                return <SidepanelCompare />;
            case 'layers':
                return <SidepanelLayers />;
            case 'properties':
                return <SidepanelAnnotationProperties />;
            case 'search':
                return <SidepanelSearch />;
            case 'signatures':
                return <SidepanelSignatures />;
            case 'thumbnails':
                return <SidepanelThumbnails />;
            default:
                return <></>;
        }
    }, [selectedSidepanel]);

    React.useMemo(() => {
        ApryseSDK.updateSelections(selections);
    }, [selections, stateName]);

    React.useMemo(() => {
        if (initError) {
            ReduxUpdater.viewMode = PdfViewerViewModeFlag.Error;
        }
    }, [initError]);

    const darkBg = isDark ? { backgroundColor: '#1e1e1e' } : {};
    const darkViewerBg = isDark ? { backgroundColor: '#141414' } : {};

    return (
        <>
            <Grid item className={styles.sidebarMenu}>
                <Paper className={styles.fullHeight} style={darkBg}>
                    <Stack className={styles.sidebarMenuButtons}>
                        <SidebarMenu />
                    </Stack>
                </Paper>
            </Grid>
            {selectedSidepanel !== null ? (
                <Grid item className={styles.sidebar}>
                    <Paper className={styles.fullHeight} style={darkBg}>
                        <Stack direction="row">
                            <Typography
                                className={styles.sidebarTitle}
                                style={isDark ? { color: '#e0e0e0' } : {}}
                            >
                                {i18next_t(`sidebar.title.${selectedSidepanel}`)}
                            </Typography>
                            <IconButton
                                className={styles.sidebarTitleGoBack}
                                onClick={() => (ReduxUpdater.selectedSidepanel = null)}
                            >
                                <GoBackOutlined />
                            </IconButton>
                        </Stack>
                        <Stack style={{ width: 'auto', flexGrow: 1 }}>{sidepanelContent}</Stack>
                    </Paper>
                </Grid>
            ) : (
                <></>
            )}
            <Grid
                item
                style={{
                    width: `calc(100vw - 68px - ${
                        selectedSidepanel === null ? 0 : GlobalConstants.sidebarWidth
                    }px`,
                    ...darkViewerBg,
                }}
            >
                <Paper style={darkViewerBg}>
                    <PdfViewer
                        className={`${styles.pdfViewer}${isDark ? ' dark-viewer' : ''}`}
                        isDark={isDark}
                        onPdfOpened={onPdfOpened}
                        onPdfError={onPdfError}
                        lic={props.license}
                        customFontURL={props.customFontURL}
                    />
                </Paper>
            </Grid>
        </>
    );
};
